import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SkillData from "../data/SkillData.json";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import SectionHeading from "../components/SectionHeading";

const LEVEL_STYLES = {
  Experienced: {
    border: "rgba(34,197,94,0.4)",
    bg: "rgba(34,197,94,0.08)",
    color: "#4ade80",
    dot: "#22c55e",
  },
  Intermediate: {
    border: "rgba(99,102,241,0.4)",
    bg: "rgba(99,102,241,0.08)",
    color: "#818cf8",
    dot: "#6366f1",
  },
  Beginner: {
    border: "rgba(251,191,36,0.4)",
    bg: "rgba(251,191,36,0.08)",
    color: "#fbbf24",
    dot: "#f59e0b",
  },
};

function SkillBadge({ skill, index, visible }) {
  const s = LEVEL_STYLES[skill.level] || LEVEL_STYLES.Intermediate;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.6,
        borderRadius: "20px",
        border: `1px solid ${s.border}`,
        bgcolor: s.bg,
        color: s.color,
        fontSize: "0.82rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        cursor: "default",
        opacity: visible ? undefined : 0,
        animation: visible ? "fadeInUp 0.4s ease both" : "none",
        animationDelay: visible ? `${index * 50}ms` : undefined,
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 4px 14px ${s.border}`,
        },
      }}
    >
      <Box
        component="span"
        sx={{ fontSize: "1rem", lineHeight: 1, flexShrink: 0 }}
      >
        {skill.icon}
      </Box>
      {skill.skill}
    </Box>
  );
}

function SkillCategory({ title, skills, visible }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: "none",
        background:
          "linear-gradient(135deg, rgba(17,17,23,0.97) 0%, rgba(9,9,11,0.97) 100%)",
        backdropFilter: "blur(12px)",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2.5,
            fontWeight: 600,
            color: "primary.light",
            textAlign: "center",
            fontSize: { xs: "1rem", md: "1.1rem" },
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {skills.map((skill, i) => (
            <SkillBadge key={i} skill={skill} index={i} visible={visible} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function SkillsContainer() {
  const { t } = useTranslation();
  const { ref, visible } = useScrollAnimation();

  return (
    <Box
      id="skills"
      ref={ref}
      sx={{
        py: { xs: 4, md: 8 },
        px: 2,
        scrollMarginTop: { xs: "56px", md: "64px" },
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(40px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      <SectionHeading
        tag="skills"
        title={t("skills.title")}
        number={2}
        mb={{ xs: 2, md: 3 }}
      />

      {/* Level legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 2, sm: 3 },
          mb: { xs: 3, md: 5 },
          flexWrap: "wrap",
        }}
      >
        {Object.entries(LEVEL_STYLES).map(([level, s]) => (
          <Box
            key={level}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: s.dot,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: s.color, fontWeight: 600, fontSize: "0.78rem" }}
            >
              {level}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          gap: { xs: 2, md: 3 },
        }}
      >
        {[
          { title: t("skills.frontend"), skills: SkillData.frontend },
          { title: t("skills.backend"), skills: SkillData.backend },
          { title: t("skills.databases"), skills: SkillData.databases },
          { title: t("skills.tools"), skills: SkillData.tools },
        ].map(({ title, skills }) => (
          <Box
            key={title}
            sx={{
              background:
                "linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(34,211,238,0.25) 100%)",
              borderRadius: "13px",
              p: "1px",
            }}
          >
            <SkillCategory title={title} skills={skills} visible={visible} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default SkillsContainer;
